import { queryClient, trpc } from "@/utils/trpc";
import { useMutation } from "@tanstack/react-query";

export const mimeFromExt = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "tsx":
      case "ts":
      case "jsx":
      case "js":
      case "json":
      case "md":
      case "txt":
      case "css":
      case "html":
        return "text/plain";
      default:
        return "";
    }
};

export type PendingAttachment = {
    id: string;
	file: File;
	previewUrl?: string;
};

export type LinkifyOptions = {
    onLinkClick?: (
        event: React.MouseEvent<HTMLAnchorElement>,
        href: string,
    ) => void;
};

export function linkifyText(text: string, options?: LinkifyOptions): React.ReactNode {
    if (!text) return text;

    const onLinkClick = options?.onLinkClick;
    const urlRegex = /(?:https?:\/\/|www\.)[^\s]+/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlRegex.exec(text)) !== null) {
        const start = match.index;
        const raw = match[0];

        if (start > lastIndex) {
            parts.push(text.slice(lastIndex, start));
        }

        let url = raw;
        let suffix = "";
        
        const trailing = raw.match(/[)\].,!?;:]+$/);
        if (trailing) {
            url = raw.slice(0, -trailing[0].length);
            suffix = trailing[0];
        }

        if (!url) {
            parts.push(raw);
            lastIndex = start + raw.length;
            continue;
        }

        const href = url.startsWith("http://") || url.startsWith("https://")
            ? url
            : `https://${url}`;

        parts.push(
            <a
                key={`${start}-${url}`}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-blue-400 hover:underline break-all"
                onClick={onLinkClick ? (event) => onLinkClick(event, href) : undefined}
            >
                {url}
            </a>
        );

        if (suffix) parts.push(suffix);
        lastIndex = start + raw.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
}

export function handleDropInput(opts: {
    e: React.DragEvent;
    setDraggingFile: React.Dispatch<React.SetStateAction<boolean>>;
    setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>;
}) {
    const { e, setDraggingFile, setPendingAttachments } = opts;

    e.preventDefault();
    setDraggingFile(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    const addAttachment = (file: File) => {
        const mime = file.type || mimeFromExt(file.name) || "application/octet-stream";
        const blobUrl = URL.createObjectURL(file);

        return {
            id: crypto.randomUUID(),
            file,
            previewUrl: blobUrl
        }
    };

    setPendingAttachments(prev =>
        prev.concat(files.map(addAttachment))
    );
}
  
export function handlePasteAttachments(opts: {
    e: React.ClipboardEvent;
    setDraggingFile: React.Dispatch<React.SetStateAction<boolean>>;
    setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>;
}) {
    const { e, setDraggingFile, setPendingAttachments } = opts;
  
    const items = Array.from(e.clipboardData.items ?? []);
    if (!items.length) return;
  
    const files = items
        .filter((it) => it.kind === "file")
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
  
    if (!files.length) return;
  
    e.preventDefault();
    setDraggingFile(false);
  
    const addAttachment = (file: File): PendingAttachment => {
        const mime = file.type || mimeFromExt(file.name) || "application/octet-stream";
        const blobUrl = URL.createObjectURL(file);
    
        return {
            id: crypto.randomUUID(),
            file,
            previewUrl: blobUrl,
        };
    };
  
    setPendingAttachments((prev) => prev.concat(files.map(addAttachment)));
}
